import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Subscription} from 'rxjs';
import {BookingState} from '../types/booking-state';
import {BookingV2StateService} from './booking-v2-state.service';
import {ToolsService} from '../../../services/tools.service';
import {BookingV2ManagerService} from './booking-v2-manager.service';
import * as dayjs from 'dayjs';
import {Dayjs} from 'dayjs';
import 'dayjs/locale/sv';
import * as isoWeek from 'dayjs/plugin/isoWeek';
import {AlertStateEnum} from '../enums/booking-enums';

@Injectable({
  providedIn: 'any',
})
export class BookingV2CalendarService implements OnDestroy {
  public weekLabel$: BehaviorSubject<string> = new BehaviorSubject('');
  public weekNumberLabel$: BehaviorSubject<string> = new BehaviorSubject('');
  public isFirstWeek$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public providerDetails$: BehaviorSubject<string> = new BehaviorSubject('');

  private dateNow: Dayjs;
  private state: BookingState;
  private subscriptions: Subscription[] = [];

  constructor(
    private tools: ToolsService,
    private bookingState: BookingV2StateService,
    private bookingManager: BookingV2ManagerService
  ) {
    dayjs.locale('sv');
    dayjs.extend(isoWeek);
    this.dateNow = dayjs();
    this.setListeners();
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  public previousWeek(): void {
    const newState: BookingState = this.tools.clone(this.state);
    newState.alert = AlertStateEnum.NORMAL;
    newState.calendarWeekStartDate = dayjs(newState.calendarWeekStartDate)
      .subtract(1, 'week')
      .format('YYYY-MM-DD');
    newState.calendarWeekEndDate = dayjs(newState.calendarWeekEndDate)
      .subtract(1, 'week')
      .format('YYYY-MM-DD');
    newState.forceCalendarDate = true;
    this.bookingManager.navigateToNewState(newState, true);
  }

  public nextWeek(): void {
    const newState: BookingState = this.tools.clone(this.state);
    newState.alert = AlertStateEnum.NORMAL;
    newState.calendarWeekStartDate = dayjs(newState.calendarWeekStartDate)
      .add(1, 'week')
      .format('YYYY-MM-DD');
    newState.calendarWeekEndDate = dayjs(newState.calendarWeekEndDate)
      .add(1, 'week')
      .format('YYYY-MM-DD');
    newState.forceCalendarDate = true;
    this.bookingManager.navigateToNewState(newState, true);
  }

  public isToday(d: string): boolean {
    return this.dateNow.format('YYYY-MM-DD') === d;
  }

  private updateSelectedWeekLabel(): void {
    this.weekLabel$.next(
      dayjs(this.state.calendarWeekStartDate).month() ===
        dayjs(this.state.calendarWeekEndDate).month()
        ? `${dayjs(this.state.calendarWeekStartDate).format('DD')} - ${dayjs(
            this.state.calendarWeekEndDate
          ).format('DD MMM')}`
        : `${dayjs(this.state.calendarWeekStartDate).format(
            'DD MMM'
          )} - ${dayjs(this.state.calendarWeekEndDate).format('DD MMM')}`
    );
  }

  private updateSelectedWeekNumberLabel(): void {
    this.weekNumberLabel$.next(
      `Vecka ${dayjs(this.state.calendarWeekStartDate).isoWeek()}`
    );
  }

  private updateIsFirstWeek(): void {
    this.isFirstWeek$.next(
      this.dateNow.isoWeek() ===
        dayjs(this.state.calendarWeekStartDate).isoWeek() &&
        dayjs(this.state.calendarWeekStartDate).year() <= this.dateNow.year()
    );
  }

  private handleState(state: BookingState): void {
    this.state = state;
    this.updateSelectedWeekLabel();
    this.updateSelectedWeekNumberLabel();
    this.updateIsFirstWeek();
  }

  private setListeners(): void {
    this.subscriptions.push(
      this.bookingState.state$.subscribe((result) => this.handleState(result))
    );
  }
}
